function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { ConfirmationModal, Row, TextButton, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { getBillClientServiceOptions, getTimeActivitiesServiceOptions } from "../../business-logic/billServiceSelector";
import BillChargeTableItem from "./BillChargeTableItem/BillChargeTableItem";
import { createEmptyChargeItem, createEmptyServiceItem, createServiceItemWithStages, getStageNote, getSubmitChargesData } from "./BillClientItem.utils";
import { billClientLoader, BillingType } from "./BillClientModal.types";
import BillingDate from "./BillingDate/BillingDate";
import { ctaTextMap, normalizeEffectiveDate } from "./BillingDate/BillingDate.utils";
import { useBillingModeData } from "./BillingDate/useBillingModeData";
import BillServiceTableItem from "./BillServiceTableItem/BillServiceTableItem";
import { useIoContext, useStore, useStoreActions } from "../../contexts";
import { useNonNullableRelationship } from "../../hooks/reactQuery/queries/useRelationship";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { handleRequest } from "../../infra/utils";
import { relationshipModel, TimeActivitiesModel } from "../../models";
import { useBillingObservable } from "../../providers/BillingObservableProvider";
import { BIRelationshipScopeProvider } from "../../providers/BIRelationshipScopeProvider";
import { selectors } from "../../store/state-selectors";
import AttachFiles from "../AttachFiles/AttachFiles";
import { Attachments } from "../Attachments/Attachments";
import AddNewItemButtons from "./AddNewItemButtons/AddNewItemButtons";
import styles from "./BillClientModal.module.scss";
import { getBiCode } from "./BillClientModal.utils";
import { BillClientTableAction } from "./BillClientTableAction";
import { CreditsWarningCallout, RelationshipEffectiveDateCallout } from "./Callouts/Callouts";
import { useSubmitCharges } from "./hooks/useSubmitCharges";
import TimeActivitiesDrawer from "./TimeActivitiesDrawer/TimeActivitiesDrawer";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const GetBillClientModalTID = parentTestId => getTestIds(parentTestId);
function ScopedBillClientModal(props) {
  const {
    manualServicesBilling,
    invoiceDateInfo,
    totalCredits,
    dismissModal,
    earliestBillableDate,
    defaultItem,
    controlData
  } = props;
  const {
    BillClientModalTID
  } = useMemo(() => GetBillClientModalTID(), []);
  const {
    relationship,
    optimisticUpdate
  } = useNonNullableRelationship(props.relationshipId);
  const {
    notifyToRefresh
  } = useBillingObservable();
  const overlayContext = useOverlayContext();
  const {
    logBI
  } = useStoreActions();
  const {
    getNow
  } = useIoContext();
  const {
    ATTACH_FILES
  } = useFeatureFlags();
  const allowTimeActivities = useStore(selectors.integration.allowTimeActivities);
  const [showTimeActivities, setShowTimeActivities] = useState(false);
  const timeActivities = useStore(state => {
    var _state$qboTimeActivit;
    return (_state$qboTimeActivit = state.qboTimeActivities[relationship.id]) === null || _state$qboTimeActivit === void 0 ? void 0 : _state$qboTimeActivit.data;
  }) || new TimeActivitiesModel();
  const {
    submitCharges
  } = useSubmitCharges();
  const vendorId = useStore(selectors.user.businessId);
  const userId = useStore(selectors.user.id);
  const timeActivitiesServiceOptions = useMemo(() => {
    if (!allowTimeActivities) return [];
    const options = getTimeActivitiesServiceOptions({
      services: relationship.services,
      controlData,
      defaultServiceId: defaultItem.type === BillingType.Service ? defaultItem.service.id : undefined
    });
    return options;
  }, [allowTimeActivities, controlData, defaultItem, relationship.services]);
  const {
    billModeOptions,
    defaultMode,
    defaultIssueDate
  } = useBillingModeData({
    invoiceDateInfo,
    effectiveDate: relationship.effectiveDate,
    earliestBillableDate
  });
  const form = useForm({
    mode: 'onSubmit',
    defaultValues: {
      items: [defaultItem],
      mode: defaultMode,
      issueDate: defaultIssueDate
    }
  });
  const itemsControl = useFieldArray({
    control: form.control,
    name: 'items'
  });
  const [mode, issueDate, items] = form.watch(['mode', 'issueDate', 'items']);
  const serviceOptions = useMemo(() => {
    return getBillClientServiceOptions({
      items,
      services: relationship.services,
      controlData
    });
  }, [controlData, relationship.services, items]);
  async function onSubmit(data) {
    const {
      mode,
      items
    } = data;
    const hasTimeActivity = items.some(item => item.type === BillingType.Service && item.timeActivityId);
    if (hasTimeActivity) logBI(12017, {
      vendorId,
      userId
    });
    overlayContext.startLoader(billClientLoader);
    const {
      success
    } = await handleRequest(sendRequest(data));
    if (!success) {
      overlayContext.showErrorToast('Failed to bill client', "bill-client-".concat(mode, "-error-toast"));
    } else {
      await notifyToRefresh();
      dismissModal();
      overlayContext.showSuccessToast('Charges successfully created', "bill-client-".concat(mode, "-success-toast"));
    }
    overlayContext.stopLoader(billClientLoader);
  }
  async function sendRequest(data) {
    const {
      mode,
      issueDate
    } = data;
    const biCode = getBiCode(mode, issueDate, getNow());
    logBI(biCode);
    const submitChargesData = getSubmitChargesData(data, allowTimeActivities && timeActivities.hasServices() ? timeActivities : undefined);
    return submitCharges({
      relationshipId: relationship.id,
      data: submitChargesData
    });
  }
  const handleCloseModal = () => {
    logBI(18404);
    dismissModal();
  };
  const handleDeleteItem = itemIndex => {
    logBI(18405);
    itemsControl.remove(itemIndex);
  };
  const onAddOutOfScopeCharge = () => {
    logBI(18407);
    itemsControl.append(createEmptyChargeItem());
  };
  const onAddServiceFromAgreement = _ref => {
    let {
      serviceId
    } = _ref;
    logBI(18406, {
      relationshipId: relationship.id
    });
    const service = relationship.services.find(s => s.id === serviceId);
    if (service) {
      itemsControl.append(createEmptyServiceItem(service));
    } else {
      throw new Error("Service with id ".concat(serviceId, " not found for bill client modal add service button"));
    }
  };
  const onAddServiceInStages = _ref2 => {
    let {
      serviceId,
      stageNumber,
      billingStages
    } = _ref2;
    logBI(18406, {
      relationshipId: relationship.id
    });
    const service = relationship.services.find(s => s.id === serviceId);
    if (!service) throw new Error("Service with id ".concat(serviceId, " not found for bill client modal add service button"));
    itemsControl.append(createServiceItemWithStages({
      service,
      stageNumber,
      billingStages,
      note: getStageNote({
        stagesLength: billingStages.length,
        stageNumber
      })
    }));
  };
  const renderTableItem = (item, itemIndex) => {
    switch (item.type) {
      case BillingType.Service:
        return /*#__PURE__*/_jsx(BillServiceTableItem, {
          serviceItem: item,
          itemIndex: itemIndex,
          relationshipEffectiveDate: normalizeEffectiveDate(relationship.effectiveDate),
          manualServiceBilling: manualServicesBilling[item.service.id],
          actionColumn: /*#__PURE__*/_jsx(BillClientTableAction, {
            showDeleteButton: items.length > 1,
            item: item,
            itemIndex: itemIndex,
            timeActivities: timeActivities,
            allowTimeActivities: allowTimeActivities,
            onDeleteItem: handleDeleteItem
          })
        }, item.itemId);
      case BillingType.Charge:
        return /*#__PURE__*/_jsx(BillChargeTableItem, {
          itemIndex: itemIndex,
          chargeItem: item,
          relationship: relationship,
          invoiceDateInfo: invoiceDateInfo,
          actionColumn: /*#__PURE__*/_jsx(BillClientTableAction, {
            showDeleteButton: items.length > 1,
            item: item,
            itemIndex: itemIndex,
            timeActivities: timeActivities,
            allowTimeActivities: allowTimeActivities,
            onDeleteItem: handleDeleteItem
          })
        }, item.itemId);
    }
  };
  return /*#__PURE__*/_jsx(ConfirmationModal, {
    title: "Bill Client - ".concat(relationship.customer.name),
    width: "1600px",
    primaryButtonLabel: ctaTextMap[mode],
    "data-testid": BillClientModalTID.Root(),
    textAlignCenter: false,
    onPrimaryButtonClick: form.handleSubmit(onSubmit),
    onCloseButtonClick: handleCloseModal,
    body: !!timeActivitiesServiceOptions.length && /*#__PURE__*/_jsx(TimeActivitiesDrawer, {
      open: showTimeActivities,
      relationship: relationship,
      itemsControl: itemsControl,
      serviceOptions: timeActivitiesServiceOptions,
      timeActivities: timeActivities,
      close: () => setShowTimeActivities(false),
      getValues: form.getValues,
      setRelationship: optimisticUpdate
    }),
    content: /*#__PURE__*/_jsx(FormProvider, _objectSpread(_objectSpread({}, form), {}, {
      children: /*#__PURE__*/_jsx("div", {
        className: styles.ensureNoHorizontalScrollFromDrawer,
        children: /*#__PURE__*/_jsxs("div", {
          className: styles.modalContentWrapper,
          children: [/*#__PURE__*/_jsxs("div", {
            className: styles.billModalDatesWrapper,
            children: [/*#__PURE__*/_jsx(BillingDate, {
              earliestBillableDate: earliestBillableDate,
              billModeOptions: billModeOptions,
              invoiceDateInfo: invoiceDateInfo,
              effectiveDate: relationship.effectiveDate
            }), /*#__PURE__*/_jsxs(Row, {
              gap: 16,
              alignItems: "center",
              children: [ATTACH_FILES && /*#__PURE__*/_jsx(AttachFiles, {
                onFilesUploaded: async () => {
                  return;
                }
              }), ' ', allowTimeActivities && /*#__PURE__*/_jsx(TextButton, {
                tooltip: !timeActivitiesServiceOptions.length ? {
                  title: 'There are no active unit-based services in the agreement to which time activities can be linked.',
                  placement: 'top'
                } : undefined,
                disabled: !timeActivitiesServiceOptions.length,
                weight: "normal",
                className: styles.showTimeActivitiesText,
                "data-testid": BillClientModalTID.ShowTimeActivities(),
                onClick: () => setShowTimeActivities(true),
                underline: "none",
                children: "Show time activities"
              })]
            })]
          }), /*#__PURE__*/_jsx(CreditsWarningCallout, {
            totalCredits: totalCredits,
            testid: BillClientModalTID.HasCreditsCallout()
          }), /*#__PURE__*/_jsx(RelationshipEffectiveDateCallout, {
            issueDate: issueDate,
            effectiveDate: relationship.effectiveDate,
            testid: BillClientModalTID.IssueDateBeforeRelationshipEffectiveDateCallout()
          }), items.map(renderTableItem), ATTACH_FILES && /*#__PURE__*/_jsx(Attachments, {
            files: [{
              displayName: 'receipt.pdf',
              url: 'https://example.com/receipt.pdf',
              fileSize: 1024,
              id: '1',
              loading: false
            }],
            onRemove: () => {}
          }), /*#__PURE__*/_jsx(AddNewItemButtons, {
            serviceOptions: serviceOptions,
            handleAddOutOfScopeCharge: onAddOutOfScopeCharge,
            handleAddServiceFromAgreement: onAddServiceFromAgreement,
            handleAddServiceInStages: onAddServiceInStages
          })]
        })
      })
    }))
  });
}
export default function BillClientModal(props) {
  const {
    relationship
  } = useNonNullableRelationship(props.relationshipId);
  const isApproved = relationshipModel.isApproved(relationship);
  return /*#__PURE__*/_jsx(BIRelationshipScopeProvider, {
    relationshipScope: {
      id: props.relationshipId,
      isApproved
    },
    children: /*#__PURE__*/_jsx(ScopedBillClientModal, _objectSpread({}, props))
  });
}